import { Check, ChevronDown, Confetti, ListChecks, X } from '@/components/ui/icons';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { APP_NAME, type GettingStartedStep } from '@clanfire/shared';
import { cn } from '@/lib/utils';
import { useAuth } from '@/stores/auth';
import { useGettingStarted } from '@/stores/gettingStarted';
import { useServers } from '@/stores/servers';
import { useUi } from '@/stores/ui';

const OPEN_KEY = 'clanfire:getting-started-open';
const readOpen = () => {
  try {
    return localStorage.getItem(OPEN_KEY) !== '0';
  } catch {
    return true;
  }
};

/**
 * The getting-started checklist, at the bottom of the sidebar for new accounts:
 * six first steps that tick themselves as they're done. It can be folded away,
 * hidden for good, or brought back from Settings → My Account.
 */
export function GettingStartedCard() {
  const show = useGettingStarted((s) => s.showChecklist);
  const stored = useGettingStarted((s) => s.steps);
  const setHidden = useGettingStarted((s) => s.setChecklistHidden);
  const user = useAuth((s) => s.user);
  const servers = useServers((s) => s.servers);
  const channels = useServers((s) => s.channels);
  const openModal = useUi((s) => s.openModal);
  const navigate = useNavigate();
  const [open, setOpen] = useState(readOpen);
  if (!show || !user) return null;

  const serverList = Object.values(servers);
  const official = serverList.find((s) => s.name === `${APP_NAME} Official`);
  const officialChannels = official ? Object.values(channels).filter((c) => c.serverId === official.id) : [];
  const general = officialChannels.find((c) => c.type === 'TEXT' && c.name === 'general') ?? officialChannels.find((c) => c.type === 'TEXT');
  const done: Record<GettingStartedStep, boolean> = {
    ...stored,
    avatar: stored.avatar || !!user.avatarUrl,
    twoFactor: stored.twoFactor || user.twoFactorEnabled,
    server: stored.server || serverList.length >= (official ? 2 : 1),
  };

  const steps: Array<{ id: GettingStartedStep; label: string; go: () => void }> = [
    { id: 'avatar', label: 'Add a profile picture', go: () => openModal({ type: 'userSettings', section: 'profile' }) },
    { id: 'message', label: official ? 'Say hi in #general' : 'Send your first message', go: () => official && navigate(`/app/${official.id}${general ? `/${general.id}` : ''}`) },
    { id: 'voice', label: 'Join a voice channel', go: () => official && navigate(`/app/${official.id}`) },
    { id: 'twoFactor', label: 'Turn on two-factor sign-in', go: () => openModal({ type: 'userSettings', section: 'security' }) },
    { id: 'friend', label: 'Add a friend', go: () => navigate('/app/@me') },
    { id: 'server', label: 'Create a server of your own', go: () => openModal({ type: 'createServer' }) },
  ];
  const count = steps.filter((s) => done[s.id]).length;
  const allDone = count === steps.length;
  const next = steps.find((s) => !done[s.id]);

  const toggle = () => {
    setOpen(!open);
    try {
      localStorage.setItem(OPEN_KEY, open ? '0' : '1');
    } catch {
      // Remembering whether it's folded is only a convenience.
    }
  };

  return (
    <section aria-label="Getting started" className="mx-2 mb-2 rounded-2xl border border-line bg-surface p-3 shadow-sm">
      <div className="flex items-center gap-2">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent-soft text-accent">
          {allDone ? <Confetti size={15} /> : <ListChecks size={15} />}
        </span>
        <button type="button" onClick={toggle} aria-expanded={open} className="min-w-0 flex-1 text-left">
          <span className="block truncate text-sm font-bold">{allDone ? 'You’re all set!' : 'Getting started'}</span>
          <span className="block truncate text-[11px] text-fg-muted">{allDone ? `Welcome to ${APP_NAME} 🔥` : `${count} of ${steps.length} done${!open && next ? ` · next: ${next.label.toLowerCase()}` : ''}`}</span>
        </button>
        <button type="button" onClick={toggle} aria-label={open ? 'Fold the checklist' : 'Open the checklist'} className="rounded-full p-1 text-fg-muted hover:bg-hover hover:text-fg">
          <ChevronDown size={15} className={cn('transition-transform', !open && '-rotate-90')} />
        </button>
        <button type="button" onClick={() => setHidden(true)} aria-label="Hide the checklist" title="Hide (bring it back from Settings → My Account)" className="rounded-full p-1 text-fg-muted hover:bg-hover hover:text-fg">
          <X size={15} />
        </button>
      </div>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-sunken" aria-hidden>
        <div className="h-full rounded-full bg-accent transition-[width] duration-500" style={{ width: `${(count / steps.length) * 100}%` }} />
      </div>
      {open && (
        <ul className="mt-2 flex flex-col">
          {steps.map((step) => (
            <li key={step.id}>
              <button
                type="button"
                onClick={step.go}
                disabled={done[step.id]}
                className="flex w-full items-center gap-2 rounded-lg px-1.5 py-1 text-left text-[13px] transition-colors enabled:hover:bg-hover disabled:cursor-default"
              >
                <span className={cn('flex h-4 w-4 shrink-0 items-center justify-center rounded-full border', done[step.id] ? 'border-success bg-success text-white' : 'border-line-strong')}>
                  {done[step.id] && <Check size={10} />}
                </span>
                <span className={cn('truncate', done[step.id] ? 'text-fg-muted line-through' : 'text-fg-soft')}>{step.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {allDone && (
        <button type="button" onClick={() => setHidden(true)} className="mt-2 w-full rounded-full bg-accent py-1.5 font-display text-sm text-accent-fg hover:bg-accent-hover">
          Done
        </button>
      )}
    </section>
  );
}
